import { ConnectionType, IConnectionDetailsDTO, WebinarAutoRecording } from '../../../../../../api/connections.dao';
import { TObject } from '../../../../../../infrastructure/type-extensions';
import { convertBoolTypeForMixPanel, isTrackingFieldEnabledOrAutoSync, OpenFrom } from '../../../../../../tracking-service/tracking-values';
import { AudioBridgeConnectionField } from './connection-form-body/audio-bridge-connection-form/audio-bridge-connection.form-config';
import { EnablementStatus, NONE } from '../../../../../../constants/common.constants';
import { ConnectionFields } from '../../../../../../modules/connections/config';

enum TrackingFields {
    SIP = 'sip',
    TYPE = 'type',
    LABEL = 'label',
    HOST_LINK = 'hostLink',
    DIAL_FOR_VS = 'dialForVs',
    CONNECTION_ID = 'connectionId',
    CONNECTION_LINK = 'connectionLink',
    ALTERNATIVE_LINK = 'alternativeLink',
    CONNECTION_TOPIC = 'connectionTopic',
    CONNECTION_NOTES = 'connectionNotes',
    CONNECTION_ACCOUNT = 'connectionAccount',
    MEETING_INVITATION = 'meetingInvitation',
    CONNECTION_HOST_KEY = 'connectionHostKey',
    CONNECTION_PASSWORD = 'connectionPassword',
    PODIUM_LINK = 'podiumLink',
    PODIUM_ACCESS_CODE = 'podiumAccessCode',
    PODIUM_EMBED_CODE = 'podiumEmbedCode',
    REGISTRATION_LINK = 'registrationUrl'
}

const connectionHeaderStringMap = {
    [TrackingFields.TYPE]: 'Connection type',
    [TrackingFields.LABEL]: 'Label',
    [TrackingFields.HOST_LINK]: 'Host Link',
    [TrackingFields.CONNECTION_LINK]: 'Connection Link',
    [TrackingFields.ALTERNATIVE_LINK]: 'Alternative link',
    [TrackingFields.CONNECTION_ID]: 'Connection Id',
    [TrackingFields.CONNECTION_PASSWORD]: 'Connection Password',
    [TrackingFields.CONNECTION_ACCOUNT]: 'Connection Account ',
    [TrackingFields.SIP]: 'Sip',
    [TrackingFields.CONNECTION_HOST_KEY]: 'Connection Host Key',
    [TrackingFields.DIAL_FOR_VS]: 'Dial For Vs',
    [TrackingFields.CONNECTION_TOPIC]: 'Connection Topic',
    [TrackingFields.MEETING_INVITATION]: 'Meeting Invitation',
    [TrackingFields.CONNECTION_NOTES]: 'Connection Notes',
    [TrackingFields.PODIUM_LINK]: 'Podium Link',
    [TrackingFields.PODIUM_ACCESS_CODE]: 'Podium Access Code',
    [TrackingFields.PODIUM_EMBED_CODE]: 'Podium Embed Code',
    [TrackingFields.REGISTRATION_LINK]: 'Registration Link',
};

export class MixPanelTrackingConnectionMapper {
    public static get connectionHeaderStringMap(): TObject<string> {
        return connectionHeaderStringMap;
    }

    public static connectionDataToTrackingEvent(
        details: IConnectionDetailsDTO,
        initialValues?: Partial<IConnectionDetailsDTO>,
        audioProviderName?: string,
    ): Record<string, 'Yes' | 'No' | string> {
        let eventData = {};

        switch (details.type) {
            case ConnectionType.AudioBridge:
                eventData = MixPanelTrackingConnectionMapper.composeAudioBridgeTrackingData(details, audioProviderName);
                break;
            case ConnectionType.Webinar:
                eventData = MixPanelTrackingConnectionMapper.composeWebinarTrackingData(details, initialValues);
                break;
            default:
                eventData = MixPanelTrackingConnectionMapper.composeGenericConnectionTrackingData(details, initialValues);
        }

        eventData['Open From'] = OpenFrom.Connections;
        return eventData;
    }

    private static composeAudioBridgeTrackingData(details: IConnectionDetailsDTO, audioProviderName?: string,): TObject<string> {
        const isTelephoneNumbersSelected = !!details.audioProviderId;
        if (details.meetingConnectionId && isTelephoneNumbersSelected) {
            details.participantTelephoneNumbers = '';
        }
        return {
            'Connection type': details.type,
            'Label': convertBoolTypeForMixPanel(!!details[AudioBridgeConnectionField.LABEL]),
            'Audio Bridge Name': details[AudioBridgeConnectionField.AUDIO_BRIDGE_NAME],
            'Audio Bridge ID': convertBoolTypeForMixPanel(!!details[AudioBridgeConnectionField.AUDIO_BRIDGE_ID]),
            'Telephone Number Set': convertBoolTypeForMixPanel(isTelephoneNumbersSelected),
            'Provider': details[AudioBridgeConnectionField.PROVIDER] ? details[AudioBridgeConnectionField.PROVIDER] : NONE,
            ...(isTelephoneNumbersSelected && { 'Telephone Number Set Name': audioProviderName}),
            'Participant Telephone Numbers': convertBoolTypeForMixPanel(!!details[AudioBridgeConnectionField.PARTICIPANT_TELEPHONE_NUMBERS]),
            'Participant One-Tap Dial': convertBoolTypeForMixPanel(!!details[AudioBridgeConnectionField.PARTICIPANT_ONE_TAP_DIAL]),
            'Conference ID': convertBoolTypeForMixPanel(!!details[AudioBridgeConnectionField.CONFERENCE_ID]),
            'Participant ID': convertBoolTypeForMixPanel(!!details[AudioBridgeConnectionField.PARTICIPANT_ID]),
            'Passcode': convertBoolTypeForMixPanel(!!details[AudioBridgeConnectionField.PASSCODE]),
            'Operator Telephone Number': convertBoolTypeForMixPanel(!!details[AudioBridgeConnectionField.OPERATOR_TELEPHONE_NUMBER]),
            'Operator Console Link': convertBoolTypeForMixPanel(!!details[AudioBridgeConnectionField.OPERATOR_CONSOLE_LINK]),
            'Operator PIN': convertBoolTypeForMixPanel(!!details[AudioBridgeConnectionField.OPERATOR_PIN]),
            'Connection Notes': convertBoolTypeForMixPanel(!!details[AudioBridgeConnectionField.CONNECTION_NOTES]),
            'Unique Reference': details.meetingUniqueReference?.toString() || NONE,
            'Passport Auto Reg': isTrackingFieldEnabledOrAutoSync(details.autoSyncEnabled),
            'Automatically Record': details.automaticallyRecordEnabled
                ? EnablementStatus.ENABLED_UPPERCASE
                : EnablementStatus.DISABLED_UPPERCASE,
        };
    }

    private static composeWebinarTrackingData(
        details: IConnectionDetailsDTO,
        initialValues?: Partial<IConnectionDetailsDTO>
    ): TObject<string> {
        const trackingData = MixPanelTrackingConnectionMapper.composeGenericConnectionTrackingData(details, initialValues);

        if (!initialValues) {
            trackingData['Record Webinar'] = details[ConnectionFields.AUTOMATICALLY_RECORD] === WebinarAutoRecording.Cloud
                ? EnablementStatus.ENABLED_UPPERCASE
                : EnablementStatus.DISABLED_UPPERCASE;
        }

        return trackingData;
    }

    private static composeGenericConnectionTrackingData(
        details: IConnectionDetailsDTO,
        initialValues?: Partial<IConnectionDetailsDTO>
    ): TObject<string> {
        const initData = {
            [TrackingFields.ALTERNATIVE_LINK.toString()]: '',
            [TrackingFields.CONNECTION_ACCOUNT.toString()]: '',
            [TrackingFields.CONNECTION_ID.toString()]: '',
            [TrackingFields.CONNECTION_LINK.toString()]: '',
            [TrackingFields.CONNECTION_PASSWORD.toString()]: '',
            [TrackingFields.CONNECTION_TOPIC.toString()]: '',
            [TrackingFields.CONNECTION_HOST_KEY.toString()]: '',
            [TrackingFields.HOST_LINK.toString()]: '',
            [TrackingFields.DIAL_FOR_VS.toString()]: null,
            [TrackingFields.LABEL.toString()]: '',
            [TrackingFields.MEETING_INVITATION.toString()]: '',
            [TrackingFields.SIP.toString()]: '',
            [TrackingFields.REGISTRATION_LINK.toString()]: '',
        };
        const values = Object.assign(initData, details);
        const eventData = {};
        for (const k in values) {
            if (values.hasOwnProperty(k)) {
                const key = MixPanelTrackingConnectionMapper.connectionHeaderValueToString(k);
                if (key) {
                    const value = !!(initialValues && initialValues[k])
                        ? values[k] !== initialValues[k]
                        : !!values[k];
                    eventData[key] = k === TrackingFields.TYPE.toString()
                        ? values[k].toString()
                        : MixPanelTrackingConnectionMapper.connectionValueToString(value);
                }
            }
        }

        return eventData;
    }

    private static connectionHeaderValueToString(key: string): string {
        return this.connectionHeaderStringMap[key] || '';
    }

    private static connectionValueToString(value: boolean): 'Yes' | 'No' {
        return convertBoolTypeForMixPanel(value) as ('Yes' | 'No');
    }
}
